import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

interface AuditRow {
  id: string;
  action: string;
  created_at?: string;
  changed_at?: string;
  entity_type?: string;
  table_name?: string;
  entity_id?: string;
  record_id?: string;
  old_status?: string | null;
  new_status?: string | null;
  actor_user_id?: string | null;
  changed_by?: string | null;
}

export const AuditLogTable = ({ rows, isLoading, variant }: { rows: AuditRow[]; isLoading: boolean; variant: "ops" | "ledger" }) => {
  const isMobile = useIsMobile();

  if (isLoading) return <p className="p-4 text-muted-foreground">Loading…</p>;
  if (!rows.length) return <p className="p-4 text-muted-foreground">No audit entries found.</p>;

  if (isMobile) {
    return (
      <div className="space-y-3">
        {rows.map((r) => {
          const ts = r.changed_at ?? r.created_at ?? "";
          return (
            <Card key={r.id}>
              <CardContent className="p-3">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm">{r.action}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.table_name ?? r.entity_type ?? "—"}
                    </p>
                    {variant === "ops" && (
                      <p className="text-xs text-muted-foreground">
                        {r.old_status ?? "—"} → {r.new_status ?? "—"}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-xs text-muted-foreground">
                      {ts ? format(new Date(ts), "MMM d, HH:mm") : "—"}
                    </p>
                    <p className="font-mono text-[10px] text-muted-foreground">
                      {(r.record_id ?? r.entity_id ?? "—").slice(0, 8)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{variant === "ops" ? "Table" : "Entity Type"}</TableHead>
          <TableHead>Action</TableHead>
          {variant === "ops" && <TableHead>Old → New</TableHead>}
          <TableHead>Record ID</TableHead>
          <TableHead>When</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const ts = r.changed_at ?? r.created_at ?? "";
          return (
            <TableRow key={r.id}>
              <TableCell className="font-medium">{r.table_name ?? r.entity_type ?? "—"}</TableCell>
              <TableCell>{r.action}</TableCell>
              {variant === "ops" && (
                <TableCell className="text-xs">
                  {r.old_status ?? "—"} → {r.new_status ?? "—"}
                </TableCell>
              )}
              <TableCell className="text-xs font-mono">{(r.record_id ?? r.entity_id ?? "—").slice(0, 8)}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {ts ? format(new Date(ts), "MMM d, yyyy HH:mm") : "—"}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};